// ─────────────────────────────────────────────
// src/pages/TeachPage.jsx
// Teacher application form page.
// ─────────────────────────────────────────────
import { useState, useEffect } from "react";
import { StarField, FormInput, FormSelect, Btn, SectionLabel } from "../components/UI";
import Footer from "../components/Footer";
import { sendTeacherApplication } from "../utils/email";

export default function TeachPage({ setPage }) {
  const [form, setForm] = useState({ name:"", email:"", phone:"", city:"", education:"", experience:"", why:"", resumeName:"" });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = '__job-schema';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "JobPosting",
      "title": "STEM Educator / Instructor",
      "description": "Mars Academy is seeking passionate educators and tech enthusiasts to deliver hands-on STEM enrichment programs to elementary and middle school students. Teach coding, AI, internet safety, and digital skills in a before- and after-school in-person setting in Fairfax County, Virginia and Northern Virginia.",
      "hiringOrganization": {
        "@type": "EducationalOrganization",
        "name": "Mars Academy",
        "url": "https://www.marsacademy.ai/"
      },
      "jobLocation": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Fairfax",
          "addressRegion": "VA",
          "addressCountry": "US"
        }
      },
      "employmentType": "PART_TIME",
      "skills": "Teaching, STEM Education, Coding, Robotics, AI, Working with Children"
    });
    document.head.appendChild(script);
    return () => document.getElementById('__job-schema')?.remove();
  }, []);

  const upd = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  async function handleSubmit() {
    if (!form.name || !form.email || !form.why) return;
    setSending(true);
    try {
      await sendTeacherApplication(form);
    } catch (err) {
      console.error("EmailJS error:", err);
    }
    setSending(false);
    setSubmitted(true);
  }

  if (submitted) return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", padding:"2rem", position:"relative", overflow:"hidden" }}>
      <StarField count={50} />
      <div style={{ position:"relative", zIndex:2, textAlign:"center", maxWidth:560 }}>
        <div style={{ fontSize:"4rem", marginBottom:"1.2rem", animation:"rocketBob 2s ease-in-out infinite" }}>🚀</div>
        <h2 style={{ fontFamily:"'Fredoka One',cursive", fontSize:"2.2rem", marginBottom:"1rem", color:"#FFD93D" }}>Application Received!</h2>
        <p style={{ color:"rgba(255,255,255,0.65)", fontSize:"1.05rem", lineHeight:1.8, fontWeight:600, marginBottom:"2rem" }}>
          Thanks, <strong style={{ color:"#fff" }}>{form.name}</strong>! We'll review your application and reach out to <strong style={{ color:"#FFD93D" }}>{form.email}</strong> within 5–7 business days.
        </p>
        <Btn onClick={() => setPage("home")} variant="ghost">← Back to Home</Btn>
      </div>
    </div>
  );

  return (
    <div>
      <section style={{ position:"relative", padding:"8rem 2rem 4rem", overflow:"hidden" }}>
        <StarField count={45} />
        <div style={{ maxWidth:700, margin:"0 auto", textAlign:"center", position:"relative", zIndex:2 }}>
          <SectionLabel color="#F59E0B">Join Our Team</SectionLabel>
          <h1 style={{ fontFamily:"'Fredoka One',cursive", fontSize:"clamp(2rem,5.5vw,3.6rem)", lineHeight:1.2, marginBottom:"1.2rem" }}>
            Interested in Becoming a Teacher? 👩‍🏫
          </h1>
          <p style={{ color:"rgba(255,255,255,0.62)", fontSize:"1.05rem", lineHeight:1.8, fontWeight:600, maxWidth:580, margin:"0 auto" }}>
            We're always looking for passionate educators and tech enthusiasts to join our crew. If you love working with kids and believe every child deserves a tech-forward education, we'd love to hear from you.
          </p>
        </div>
      </section>

      <section style={{ padding:"2rem 2rem 4rem", maxWidth:680, margin:"0 auto" }}>
        <div style={{ background:"rgba(10,13,48,0.85)", border:"1.5px solid rgba(255,255,255,0.12)", borderRadius:24, padding:"2.4rem", backdropFilter:"blur(12px)" }}>
          <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
            <div style={{ display:"flex", flexWrap:"wrap", gap:"1rem" }}>
              <FormInput label="Full Name" value={form.name} onChange={upd("name")} placeholder="Jane Smith" required half />
              <FormInput label="Phone" value={form.phone} onChange={upd("phone")} placeholder="(555) 000-0000" half />
            </div>
            <FormInput label="Email Address" type="email" value={form.email} onChange={upd("email")} placeholder="jane@email.com" required />
            <div style={{ display:"flex", flexWrap:"wrap", gap:"1rem" }}>
              <FormInput label="City & State" value={form.city} onChange={upd("city")} placeholder="Austin, TX" half />
              <FormSelect
                label="Education Level"
                value={form.education}
                onChange={upd("education")}
                placeholder="Select..."
                options={["High School Diploma","Associate's Degree","Bachelor's Degree","Master's Degree","Doctorate / PhD"].map((g) => ({ value:g, label:g }))}
              />
            </div>
            <FormInput label="Relevant Experience" type="textarea" value={form.experience} onChange={upd("experience")} placeholder="Teaching, tutoring, coding, STEM, working with kids..." rows={3} />
            <FormInput label="Why do you want to teach at Mars Academy? *" type="textarea" value={form.why} onChange={upd("why")} placeholder="What excites you about teaching kids technology? What would you bring to the team?" rows={4} required />

            {/* Resume upload — visual only until backend wired up */}
            <div>
              <label style={{ fontSize:"0.8rem", fontWeight:700, color:"rgba(255,255,255,0.55)", letterSpacing:"0.04em", display:"block", marginBottom:6 }}>Resume / CV</label>
              <label style={{ display:"flex", alignItems:"center", gap:12, background:"rgba(255,255,255,0.06)", border:"1.5px dashed rgba(255,255,255,0.2)", borderRadius:12, padding:"1rem 1.2rem", cursor:"pointer" }}>
                <input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setForm((p) => ({ ...p, resumeName: e.target.files[0]?.name || "" }))} style={{ display:"none" }} />
                <span style={{ fontSize:"1.4rem" }}>📎</span>
                <div>
                  <div style={{ fontWeight:700, fontSize:"0.88rem" }}>{form.resumeName || "Click to upload your resume"}</div>
                  <div style={{ color:"rgba(255,255,255,0.35)", fontSize:"0.75rem", fontWeight:600 }}>PDF, DOC, or DOCX — max 5 MB</div>
                </div>
              </label>
            </div>

            <Btn onClick={handleSubmit} variant="amber" disabled={sending} style={{ width:"100%", marginTop:"0.5rem", fontSize:"1rem" }}>{sending ? "Sending…" : "Submit Application 🚀"}</Btn>
            <p style={{ color:"rgba(255,255,255,0.25)", fontSize:"0.75rem", fontWeight:600, textAlign:"center" }}>Your information is kept private and never sold.</p>
          </div>
        </div>
      </section>

      <Footer setPage={setPage} />
    </div>
  );
}
